import type { AttractionConfig, AttractionId } from "./types";
import { ATTRACTION_IDS } from "./types";

const ATTRACTIONS: Record<AttractionId, AttractionConfig> = {
  "machu-picchu": {
    id: "machu-picchu",
    providerId: "tuboleto",
    brandName: "MachuPing",
    emailFrom: "MachuPing <alerts@machuping.com>",
    shortName: "Machu Picchu",
    publicHosts: ["machuping.com", "www.machuping.com", "localhost", "127.0.0.1"],
    stagingHost: "machuping.com",
    customerHost: "machuping.com",
    officialBookingUrl: "https://tuboleto.cultura.pe/llaqta_machupicchu",
    timezone: "America/Lima",
    locales: ["en", "es", "pt"],
    defaultLocale: "en",
    brand: {
      themeId: "machu-picchu",
      logo: { src: "/brand/machuping-logo.svg", width: 48, height: 32 },
      palette: {
        background: "#f8fbf9", surface: "#ffffff", text: "#08111f", mutedText: "#647084",
        border: "#d9dee7", accent: "#087d45", accentForeground: "#ffffff",
        action: "#087d45", actionForeground: "#ffffff", focus: "#087d45",
      },
      typography: {
        body: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        display: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        utility: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },
      radius: "0.5rem",
      surface: "clean",
      stripe: {
        colorPrimary: "#087d45", colorBackground: "#ffffff", colorText: "#08111f",
        colorTextSecondary: "#647084", colorTextPlaceholder: "#8c95a3", colorDanger: "#dc2626",
        colorSuccess: "#087d45",
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        borderRadius: "6px", focusBoxShadow: "0 0 0 3px rgba(8, 125, 69, 0.2)",
      },
    },
    email: {
      logo: { src: "/brand/machuping-logo-email.png", width: 46, height: 30 },
      footerDescription: "independent Machu Picchu availability monitor",
    },
    price: { amountCents: 900, currency: "usd", pricingVersion: "machu-tiered-v1", maxPartySize: 10 },
    sms: { enabled: true, senderId: "MachuPing", amountCents: 499, pricingVersion: "sms-low-volume-v1", maxMessages: 5, minimumIntervalHours: 24 },
    products: [],
  },
  "studio-tour-london": {
    id: "studio-tour-london",
    providerId: "warner-webstore",
    brandName: "Studio Ticket Watch",
    emailFrom: "Studio Ticket Watch <alerts@studioticketwatch.com>",
    shortName: "Studio Tour London",
    publicHosts: ["studio.machuping.com", "studioticketwatch.com", "www.studioticketwatch.com"],
    stagingHost: "studio.machuping.com",
    customerHost: "studioticketwatch.com",
    officialBookingUrl: "https://www.wbstudiotour.co.uk/tickets/",
    timezone: "Europe/London",
    locales: ["en", "fr", "de", "es"],
    defaultLocale: "en",
    brand: {
      themeId: "studio-tour-london",
      logo: { src: "/brand-assets/studio-tour-london/logo.png", width: 42, height: 42 },
      palette: {
        background: "#f4f7fb", surface: "#ffffff", text: "#071528", mutedText: "#52697e",
        border: "#aebccc", accent: "#00669f", accentForeground: "#ffffff",
        action: "#c74335", actionForeground: "#ffffff", focus: "#0077bd",
      },
      typography: {
        body: "Arial, Helvetica, sans-serif",
        display: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif',
        utility: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
      radius: "0.125rem",
      surface: "control-board",
      stripe: {
        colorPrimary: "#c74335", colorBackground: "#ffffff", colorText: "#071528",
        colorTextSecondary: "#52697e", colorTextPlaceholder: "#71869b", colorDanger: "#c93b31",
        colorSuccess: "#0077bd", fontFamily: "Arial, Helvetica, sans-serif",
        borderRadius: "2px", focusBoxShadow: "0 0 0 3px rgba(0, 119, 189, 0.22)",
      },
    },
    email: {
      logo: { src: "/brand-assets/studio-tour-london/logo.png", width: 42, height: 42 },
      footerDescription: "independent Studio Tour London availability monitor",
    },
    price: { amountCents: 1200, currency: "gbp", pricingVersion: "studio-flat-v1", maxPartySize: 4 },
    sms: { enabled: true, senderId: "StudioWatch", amountCents: 399, pricingVersion: "sms-low-volume-v1", maxMessages: 5, minimumIntervalHours: 24 },
    products: [
      {
        routeId: 1001,
        circuitId: 101,
        slug: "studio-tour-admission",
        name: "Warner Bros. Studio Tour admission",
        providerProductCode: "studio-tour-admission",
        requiresTimeSlot: true,
      },
    ],
  },
  "statue-liberty-crown": {
    id: "statue-liberty-crown",
    providerId: "statue-city-experiences",
    brandName: "Crown Watch NYC",
    emailFrom: "Crown Watch NYC <alerts@crownwatchnyc.com>",
    shortName: "Statue of Liberty Crown",
    publicHosts: ["crown.machuping.com", "crownwatchnyc.com", "www.crownwatchnyc.com"],
    stagingHost: "crown.machuping.com",
    customerHost: "crownwatchnyc.com",
    officialBookingUrl: "https://www.cityexperiences.com/new-york/city-cruises/statue/",
    timezone: "America/New_York",
    locales: ["en", "es"],
    defaultLocale: "en",
    brand: {
      themeId: "statue-liberty-crown",
      logo: { src: "/brand-assets/statue-liberty-crown/logo.png", width: 42, height: 42 },
      palette: {
        background: "#f5f1e4", surface: "#fffdf6", text: "#102d3b", mutedText: "#4a6570",
        border: "#afc3ba", accent: "#2d6f62", accentForeground: "#ffffff",
        action: "#b53e2c", actionForeground: "#ffffff", focus: "#b53e2c",
      },
      typography: {
        body: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif',
        display: 'Georgia, "Times New Roman", serif',
        utility: "ui-monospace, SFMono-Regular, Consolas, monospace",
      },
      radius: "0rem",
      surface: "civic",
      stripe: {
        colorPrimary: "#b53e2c", colorBackground: "#fffdf6", colorText: "#102d3b",
        colorTextSecondary: "#4a6570", colorTextPlaceholder: "#6e858d", colorDanger: "#b53e2c",
        colorSuccess: "#2d6f62", fontFamily: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif',
        borderRadius: "0px", focusBoxShadow: "0 0 0 3px rgba(181, 62, 44, 0.2)",
      },
    },
    email: {
      logo: { src: "/brand-assets/statue-liberty-crown/logo.png", width: 42, height: 42 },
      footerDescription: "independent Statue of Liberty Crown availability monitor",
    },
    price: { amountCents: 800, currency: "usd", pricingVersion: "crown-flat-v1", maxPartySize: 4 },
    sms: { enabled: true, senderId: "CrownNYC", amountCents: 499, pricingVersion: "sms-low-volume-v1", maxMessages: 5, minimumIntervalHours: 24 },
    products: [
      {
        routeId: 2001,
        circuitId: 201,
        slug: "crown-reserve",
        name: "Statue of Liberty Crown Reserve",
        providerProductCode: "crown-reserve",
        requiresTimeSlot: true,
      },
    ],
  },
  "alhambra-nasrid": {
    id: "alhambra-nasrid",
    providerId: "iacpos",
    brandName: "Granada Slot",
    emailFrom: "Granada Slot <alerts@granadaslot.com>",
    shortName: "Alhambra and Nasrid Palaces",
    publicHosts: ["granada.machuping.com", "granadaslot.com", "www.granadaslot.com"],
    stagingHost: "granada.machuping.com",
    customerHost: "granadaslot.com",
    officialBookingUrl: "https://tickets.alhambra-patronato.es/",
    timezone: "Europe/Madrid",
    locales: ["es", "en", "de", "fr"],
    defaultLocale: "es",
    brand: {
      themeId: "alhambra-nasrid",
      logo: { src: "/brand-assets/alhambra-nasrid/logo.png", width: 44, height: 44 },
      palette: {
        background: "#f4efe5", surface: "#fffdf8", text: "#18342f", mutedText: "#52635f",
        border: "#c7d1cc", accent: "#0b6170", accentForeground: "#ffffff",
        action: "#a53f2f", actionForeground: "#ffffff", focus: "#0b6170",
      },
      typography: {
        body: "Arial, Helvetica, sans-serif", display: "Georgia, serif",
        utility: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
      radius: "0.125rem",
      surface: "plaster",
      stripe: {
        colorPrimary: "#a53f2f", colorBackground: "#fffdf8", colorText: "#18342f",
        colorTextSecondary: "#52635f", colorTextPlaceholder: "#76847f", colorDanger: "#a53f2f",
        colorSuccess: "#0b6170", fontFamily: "Arial, Helvetica, sans-serif",
        borderRadius: "2px", focusBoxShadow: "0 0 0 3px rgba(11, 97, 112, 0.2)",
      },
    },
    email: {
      logo: { src: "/brand-assets/alhambra-nasrid/logo.png", width: 44, height: 44 },
      footerDescription: "independent Alhambra and Nasrid Palaces availability monitor",
    },
    price: { amountCents: 900, currency: "eur", pricingVersion: "granada-flat-v1", maxPartySize: 4 },
    sms: { enabled: true, senderId: "GranadaSlot", amountCents: 449, pricingVersion: "sms-low-volume-v1", maxMessages: 5, minimumIntervalHours: 24 },
    products: [
      {
        routeId: 3001,
        circuitId: 301,
        slug: "alhambra-general-nasrid",
        name: "Alhambra General with Nasrid Palaces",
        providerProductCode: "alhambra-general",
        requiresTimeSlot: true,
      },
    ],
  },
  "anne-frank-house": {
    id: "anne-frank-house",
    providerId: "anne-frank",
    brandName: "Amsterdam Museum Slot",
    emailFrom: "Amsterdam Museum Slot <alerts@amsterdammuseumslot.com>",
    shortName: "Anne Frank House",
    publicHosts: ["amsterdam.machuping.com", "amsterdammuseumslot.com", "www.amsterdammuseumslot.com"],
    stagingHost: "amsterdam.machuping.com",
    customerHost: "amsterdammuseumslot.com",
    officialBookingUrl: "https://www.annefrank.org/en/museum/tickets/",
    timezone: "Europe/Amsterdam",
    locales: ["en", "nl", "de", "fr"],
    defaultLocale: "en",
    brand: {
      themeId: "anne-frank-house",
      logo: { src: "/brand-assets/anne-frank-house/logo.png", width: 44, height: 44 },
      palette: {
        background: "#f4f1e9", surface: "#fffdf8", text: "#172a35", mutedText: "#526168",
        border: "#bfc2bf", accent: "#315c70", accentForeground: "#ffffff",
        action: "#315c70", actionForeground: "#ffffff", focus: "#9a493b",
      },
      typography: {
        body: 'Arial, "Helvetica Neue", sans-serif', display: "Georgia, serif",
        utility: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
      radius: "0.125rem",
      surface: "archive",
      stripe: {
        colorPrimary: "#315c70", colorBackground: "#fffdf8", colorText: "#172a35",
        colorTextSecondary: "#526168", colorTextPlaceholder: "#6b787d", colorDanger: "#9a493b",
        colorSuccess: "#315c70", fontFamily: 'Arial, "Helvetica Neue", sans-serif',
        borderRadius: "2px", focusBoxShadow: "0 0 0 3px rgba(154, 73, 59, 0.2)",
      },
    },
    email: {
      logo: { src: "/brand-assets/anne-frank-house/logo.png", width: 44, height: 44 },
      footerDescription: "independent Anne Frank House availability monitor",
    },
    price: { amountCents: 600, currency: "eur", pricingVersion: "anne-flat-v1", maxPartySize: 4 },
    sms: { enabled: true, senderId: "MuseumSlot", amountCents: 449, pricingVersion: "sms-low-volume-v1", maxMessages: 5, minimumIntervalHours: 24 },
    products: [
      {
        routeId: 4001,
        circuitId: 401,
        slug: "museum-entry",
        name: "Anne Frank House museum entry",
        providerProductCode: "tickets",
        requiresTimeSlot: true,
      },
      {
        routeId: 4002,
        circuitId: 401,
        slug: "introductory-program",
        name: "Anne Frank House introductory program",
        providerProductCode: "introductory-program",
        requiresTimeSlot: true,
        publicMonitoringEnabled: false,
      },
    ],
  },
};

const HOST_TO_ATTRACTION = new Map<string, AttractionId>();
const ANALYTICS_HOST_TO_ATTRACTION = new Map<string, AttractionId>();
for (const attraction of Object.values(ATTRACTIONS)) {
  for (const host of attraction.publicHosts) HOST_TO_ATTRACTION.set(host, attraction.id);
  ANALYTICS_HOST_TO_ATTRACTION.set(`t.${attraction.customerHost}`, attraction.id);
}
export function attractionById(id: AttractionId): AttractionConfig {
  return ATTRACTIONS[id];
}

export function isAttractionId(value: unknown): value is AttractionId {
  return typeof value === "string" && (ATTRACTION_IDS as readonly string[]).includes(value);
}

export function attractionFromHost(rawHost: string | null | undefined): AttractionConfig | null {
  const host = (rawHost ?? "").trim().toLowerCase().replace(/:\d+$/, "");
  const id = HOST_TO_ATTRACTION.get(host);
  return id ? attractionById(id) : null;
}

export function analyticsHostForAttraction(attraction: AttractionConfig): string {
  return `t.${attraction.customerHost}`;
}

export function attractionFromAnalyticsHost(rawHost: string | null | undefined): AttractionConfig | null {
  const host = (rawHost ?? "").trim().toLowerCase().replace(/:\d+$/, "");
  const id = ANALYTICS_HOST_TO_ATTRACTION.get(host);
  return id ? attractionById(id) : null;
}

export function allAttractions(): readonly AttractionConfig[] {
  return ATTRACTION_IDS.map(attractionById);
}

export function attractionForRouteId(routeId: number): AttractionConfig | null {
  if (routeId >= 7 && routeId <= 16) return ATTRACTIONS["machu-picchu"];
  return allAttractions().find((attraction) => attraction.products.some((product) => product.routeId === routeId)) ?? null;
}
